import React from 'react'

import ConfirmationModal from 'components/common/ConfirmationModal'
import TextArea from 'components/common/TextArea'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { track } from 'track'
import { messageFromResponseError, capitalize } from 'utils/strings'

type HaltConfirmationProps = {
  open: boolean
  onCancel: () => void
  guid?: string
  fetchAttack?: (guid: string, currentTeamId: string) => void
  title: string
  doFetch?: () => void
  trackingOptions: Record<string, unknown>
  trackedEvent: string
  type: string
  onConfirmRequest: ({ reason }: { reason: string }) => void
  showHaltReason?: boolean
  message?: React.ReactNode
  showConfirmationContent?: boolean
  hideFooter?: boolean
}

const HaltConfirmation: React.FC<HaltConfirmationProps> = ({
  guid,
  fetchAttack,
  doFetch,
  open,
  title,
  trackingOptions,
  trackedEvent,
  onConfirmRequest,
  type,
  onCancel,
  showHaltReason = true,
  message = '',
  showConfirmationContent = true,
  hideFooter,
  ...rest
}) => {
  const { currentTeamId } = useAuth()
  const [reason, setReason] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  return (
    <ConfirmationModal
      confirmText={isSubmitting ? 'Halting...' : 'Confirm'}
      disabled={isSubmitting}
      hideFooter={hideFooter}
      message={
        <div>
          {message}
          {showConfirmationContent &&
            (showHaltReason ? (
              <TextArea
                data-test-input="reason"
                name="reason"
                placeholder="Optional Reason"
                value={reason}
                onChange={({ target: { value } }) => setReason(value)}
              />
            ) : (
              `Are you sure you want to halt this ${type}?`
            ))}
        </div>
      }
      open={open}
      title={title}
      onCancel={onCancel}
      onConfirm={async () => {
        if (isSubmitting) {
          return
        }

        try {
          setIsSubmitting(true)
          await onConfirmRequest({ reason })

          track(`${trackedEvent}`, trackingOptions)

          toast({
            text: `${capitalize(type)} successfully halted`,
            type: 'success'
          })
        } catch (err) {
          toast({
            type: 'error',
            text: messageFromResponseError(
              err,
              `There was an issue halting this ${type}. Please try again later.`
            )
          })
        }
        if (fetchAttack) await fetchAttack(guid, currentTeamId)
        if (doFetch) await doFetch()
        setReason('')
        onCancel()
        setIsSubmitting(false)
      }}
      {...rest}
    />
  )
}
export default HaltConfirmation
